import { useCallback, useMemo } from 'react';

import { type ApplicationState, useApplicationState } from '@lib/state';

const selectStoreDirectory = (state: ApplicationState) => state.storeDirectory;

interface UseStoreDirectoryParams {
    cateringOnly?: boolean;
    defaultStoreId?: number | null;
}

const useStoreDirectory = ({ cateringOnly, defaultStoreId }: UseStoreDirectoryParams = {}) => {
    const { stores: unfilteredStores, ...state } = useApplicationState(selectStoreDirectory);

    const stores = useMemo(() => {
        const hubsRemoved = unfilteredStores.filter((s) => !s.isHub);

        if (cateringOnly) {
            return hubsRemoved.filter((s) => s.hasCatering);
        }

        return hubsRemoved;
    }, [unfilteredStores, cateringOnly]);

    // Get a reference to the full application state getter to use in callbacks
    const getState = useApplicationState.getState;

    const selectStore = useCallback(
        (id: number) => {
            // Get the latest state at call time, not creation time
            const currentState = getState();
            const latestStores = currentState.storeDirectory.stores.filter((s) => !s.isHub);

            return latestStores.find((s) => s.id === id);
        },
        [] // No dependencies needed since we're using getState()
    );

    const { sortedStores, matchedDefault } = useMemo(() => {
        if (!stores.length) {
            return { sortedStores: stores, matchedDefault: undefined };
        }

        if (defaultStoreId == null) {
            return { sortedStores: stores, matchedDefault: undefined };
        }

        const idx = stores.findIndex((s) => s.id === defaultStoreId);
        if (idx < 0) {
            return { sortedStores: stores, matchedDefault: undefined };
        }

        const matchedDefault = stores[idx];
        if (idx === 0) {
            return { sortedStores: stores, matchedDefault };
        }

        return {
            sortedStores: [matchedDefault, ...stores.slice(0, idx), ...stores.slice(idx + 1)],
            matchedDefault
        };
    }, [stores, defaultStoreId]);

    return {
        ...state,
        selectStore,
        stores,
        sortedStores,
        matchedDefault
    };
};

export default useStoreDirectory;
